import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getConfig, saveConfig, getRecentGames, getDauAnalytics, startBot, adminGrantCards, adminClearCollection, adminGrantCoins, adminListPlayers, adminLoginAsNewUser, adminWipePlayerData } from '../../services/api/apiService';

import './style.css';

const WIPE_CONFIRMATION = 'WIPE ALL PLAYER DATA';

const AdminDashboardPage = () => {
    const [isLoadingConfig, setIsLoadingConfig] = useState(true);
    const [isLoadingRecentGames, setIsLoadingRecentGames] = useState(true);
    const [isLoadingDauAnalytics, setIsLoadingDauAnalytics] = useState(true);
    const [config, setConfig] = useState(null);
    const [recentGames, setRecentGames] = useState(null);
    const [dauAnalytics, setDauAnalytics] = useState(null);
    const [isStartingBot, setIsStartingBot] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchConfig = async () => {
            const config = await getConfig();
            setConfig(config);
            setIsLoadingConfig(false);
        };

        const fetchRecentGames = async () => {
            const recentGames = await getRecentGames();
            setRecentGames(recentGames);
            setIsLoadingRecentGames(false);
        };

        const fetchDauAnalytics = async () => {
            const dau = await getDauAnalytics();
            setDauAnalytics(dau);
            setIsLoadingDauAnalytics(false);
        };

        fetchConfig();
        fetchRecentGames();
        fetchDauAnalytics();
    }, []);

    const OpenAlphaToggle = () => {
        const [isSaving, setIsSaving] = useState(false);
        const openAlphaValue = config?.find(config => config.key === 'open_alpha')?.value;
        const [isOpenAlpha, setIsOpenAlpha] = useState(openAlphaValue === true);

        const save = async (value) => {
            setIsSaving(true);
            await saveConfig('open_alpha', value);
            setIsOpenAlpha(value);
            setIsSaving(false);
        };

        return (
            <div style={{ margin: '1em' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '1em', fontSize: '1.15em', cursor: isSaving ? 'not-allowed' : 'pointer' }}>
                    <span style={{ fontWeight: 500 }}>Enable Open Alpha</span>
                    <span style={{ position: 'relative', display: 'inline-block', width: 48, height: 28 }}>
                        <input
                            type="checkbox"
                            checked={isOpenAlpha}
                            disabled={isSaving}
                            onChange={() => save(!isOpenAlpha)}
                            style={{
                                opacity: 0,
                                width: 0,
                                height: 0,
                            }}
                        />
                        <span
                            style={{
                                position: 'absolute',
                                cursor: isSaving ? 'not-allowed' : 'pointer',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: isOpenAlpha ? '#1976d2' : '#ccc',
                                transition: 'background 0.2s',
                                borderRadius: 16,
                                boxShadow: isOpenAlpha ? '0 0 2px #1976d2' : '0 0 2px #ccc',
                            }}
                        />
                        <span
                            style={{
                                position: 'absolute',
                                left: isOpenAlpha ? '22px' : '3px',
                                top: '3px',
                                width: '22px',
                                height: '22px',
                                background: '#fff',
                                borderRadius: '50%',
                                transition: 'left 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                                border: '1px solid #ddd'
                            }}
                        />
                    </span>
                    {isSaving && <span style={{ marginLeft: 8, color: '#666', fontSize: '0.93em' }}>Saving...</span>}
                </label>
            </div>
        );
    };

    const StartBotButtons = () => {
        const startBotFunction = async (deckId) => {
            setIsStartingBot(true);
            await startBot(deckId);
            setIsStartingBot(false);
        };

        return (
            <div className='start-bot-buttons'>
                <button onClick={() => startBotFunction('ST01')} disabled={isStartingBot}>
                    {isStartingBot ? 'Starting...' : 'Start ST01 Bot'}
                </button>
                <button onClick={() => startBotFunction('ST02')} disabled={isStartingBot}>
                    {isStartingBot ? 'Starting...' : 'Start ST02 Bot'}
                </button>
                <button onClick={() => startBotFunction('ST03')} disabled={isStartingBot}>
                    {isStartingBot ? 'Starting...' : 'Start ST03 Bot'}
                </button>
                <button onClick={() => startBotFunction('ST04')} disabled={isStartingBot}>
                    {isStartingBot ? 'Starting...' : 'Start ST04 Bot'}
                </button>
            </div>
        );
    };

    const LoginAsNewUserButton = () => {
        const [isLoggingIn, setIsLoggingIn] = useState(false);

        const loginAsNewUser = async () => {
            setIsLoggingIn(true);
            try {
                localStorage.removeItem('selectedDeck');
                await adminLoginAsNewUser();
                window.location.assign('/');
            } catch (error) {
                console.error('Failed to login as new user:', error);
                alert(error.response?.data?.error || error.message || 'Login failed');
                setIsLoggingIn(false);
            }
        };

        return (
            <div className="login-as-new-user-button">
                <button onClick={loginAsNewUser} disabled={isLoggingIn}>
                    {isLoggingIn ? 'Logging in...' : 'Login as new user'}
                </button>
            </div>
        );
    };

    const EffectsEditorButton = () => {
        return (
            <div className='effects-editor-button'>
                <button onClick={() => navigate('/effects-editor')}>
                    Effects Editor
                </button>
            </div>
        );
    };

    const CollectionTools = () => {
        const [players, setPlayers] = useState([]);
        const [grantUserId, setGrantUserId] = useState('');
        const [grantCardId, setGrantCardId] = useState('');
        const [grantCount, setGrantCount] = useState(1);
        const [clearUserId, setClearUserId] = useState('');
        const [status, setStatus] = useState('');
        const [isBusy, setIsBusy] = useState(false);

        useEffect(() => {
            let cancelled = false;
            const loadPlayers = async () => {
                try {
                    const data = await adminListPlayers();
                    if (!cancelled) {
                        setPlayers(Array.isArray(data) ? data : []);
                    }
                } catch (error) {
                    console.error('Failed to load players:', error);
                }
            };
            loadPlayers();
            return () => { cancelled = true; };
        }, []);

        const playerListId = 'admin-collection-players';

        const grantCard = async (e) => {
            e.preventDefault();
            setIsBusy(true);
            setStatus('');
            try {
                const count = Number(grantCount);
                await adminGrantCards(grantUserId.trim(), [{ id: grantCardId.trim(), count }]);
                setStatus(`Granted ${count}x ${grantCardId.trim()} to ${grantUserId.trim()}`);
            } catch (error) {
                setStatus(error.response?.data?.error || error.message || 'Grant failed');
            } finally {
                setIsBusy(false);
            }
        };

        const clearCollection = async (e) => {
            e.preventDefault();
            const userId = clearUserId.trim();
            if (!userId) {
                setStatus('userId is required');
                return;
            }
            if (!window.confirm(`Clear collection for ${userId}?`)) {
                return;
            }
            setIsBusy(true);
            setStatus('');
            try {
                await adminClearCollection(userId);
                setStatus(`Cleared collection for ${userId}`);
            } catch (error) {
                setStatus(error.response?.data?.error || error.message || 'Clear failed');
            } finally {
                setIsBusy(false);
            }
        };

        return (
            <div className="collection-tools">
                <h2>Collection</h2>
                <datalist id={playerListId}>
                    {players.map((player) => (
                        <option
                            key={player.id}
                            value={player.id}
                            label={player.username ? `${player.username} (${player.id})` : player.id}
                        >
                            {player.username ? `${player.username} (${player.id})` : player.id}
                        </option>
                    ))}
                </datalist>
                <form className="collection-tool-form" onSubmit={grantCard}>
                    <h3>Grant card</h3>
                    <label>
                        User ID
                        <input
                            value={grantUserId}
                            onChange={(e) => setGrantUserId(e.target.value)}
                            list={playerListId}
                            required
                            disabled={isBusy}
                            placeholder="Start typing a user id"
                            autoComplete="off"
                        />
                    </label>
                    <label>
                        Card ID
                        <input value={grantCardId} onChange={(e) => setGrantCardId(e.target.value)} required disabled={isBusy} placeholder="OP01-001" />
                    </label>
                    <label>
                        Count
                        <input type="number" min={1} step={1} value={grantCount} onChange={(e) => setGrantCount(e.target.value)} required disabled={isBusy} />
                    </label>
                    <button type="submit" disabled={isBusy}>{isBusy ? 'Working...' : 'Grant'}</button>
                </form>
                <form className="collection-tool-form" onSubmit={clearCollection}>
                    <h3>Clear collection</h3>
                    <label>
                        User ID
                        <input
                            value={clearUserId}
                            onChange={(e) => setClearUserId(e.target.value)}
                            list={playerListId}
                            required
                            disabled={isBusy}
                            placeholder="Start typing a user id"
                            autoComplete="off"
                        />
                    </label>
                    <button type="submit" disabled={isBusy}>{isBusy ? 'Working...' : 'Clear'}</button>
                </form>
                {status && <div className="collection-tools-status">{status}</div>}
            </div>
        );
    };

    const WalletTools = () => {
        const [players, setPlayers] = useState([]);
        const [grantUserId, setGrantUserId] = useState('');
        const [grantAmount, setGrantAmount] = useState(100);
        const [status, setStatus] = useState('');
        const [isBusy, setIsBusy] = useState(false);

        useEffect(() => {
            let cancelled = false;
            const loadPlayers = async () => {
                try {
                    const data = await adminListPlayers();
                    if (!cancelled) {
                        setPlayers(Array.isArray(data) ? data : []);
                    }
                } catch (error) {
                    console.error('Failed to load players:', error);
                }
            };
            loadPlayers();
            return () => { cancelled = true; };
        }, []);

        const playerListId = 'admin-wallet-players';

        const grantCoins = async (e) => {
            e.preventDefault();
            const userId = grantUserId.trim();
            const amount = Number(grantAmount);
            setIsBusy(true);
            setStatus('');
            try {
                const wallet = await adminGrantCoins(userId, amount);
                setStatus(`Granted ${amount} coins to ${userId} (balance: ${wallet.coins})`);
                setPlayers((prev) => prev.map((player) => (
                    player.id === userId ? { ...player, coins: wallet.coins } : player
                )));
            } catch (error) {
                setStatus(error.response?.data?.error || error.message || 'Grant failed');
            } finally {
                setIsBusy(false);
            }
        };

        return (
            <div className="collection-tools">
                <h2>Wallet</h2>
                <datalist id={playerListId}>
                    {players.map((player) => (
                        <option
                            key={player.id}
                            value={player.id}
                            label={player.username ? `${player.username} (${player.id})` : player.id}
                        >
                            {player.username ? `${player.username} (${player.id})` : player.id}
                        </option>
                    ))}
                </datalist>
                <form className="collection-tool-form" onSubmit={grantCoins}>
                    <h3>Grant coins</h3>
                    <label>
                        User ID
                        <input
                            value={grantUserId}
                            onChange={(e) => setGrantUserId(e.target.value)}
                            list={playerListId}
                            required
                            disabled={isBusy}
                            placeholder="Start typing a user id"
                            autoComplete="off"
                        />
                    </label>
                    <label>
                        Amount
                        <input type="number" step={1} value={grantAmount} onChange={(e) => setGrantAmount(e.target.value)} required disabled={isBusy} placeholder="100 (negative deducts)" />
                    </label>
                    <button type="submit" disabled={isBusy}>{isBusy ? 'Working...' : 'Grant'}</button>
                </form>
                <h3>Balances</h3>
                <table className="recent-games-table">
                    <thead>
                        <tr>
                            <th>Player</th>
                            <th>Coins</th>
                        </tr>
                    </thead>
                    <tbody>
                        {players.map((player) => (
                            <tr key={player.id}>
                                <td>{player.username || player.id}</td>
                                <td>{player.coins}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {status && <div className="collection-tools-status">{status}</div>}
            </div>
        );
    };

    const DataWipe = () => {
        const [isOpen, setIsOpen] = useState(false);
        const [confirmation, setConfirmation] = useState('');
        const [error, setError] = useState('');
        const [isWiping, setIsWiping] = useState(false);
        const canWipe = confirmation === WIPE_CONFIRMATION && !isWiping;

        const close = () => {
            if (isWiping) return;
            setIsOpen(false);
            setConfirmation('');
            setError('');
        };

        const wipe = async (e) => {
            e.preventDefault();
            if (!canWipe) return;
            setIsWiping(true);
            setError('');
            try {
                await adminWipePlayerData(confirmation);
                localStorage.removeItem('selectedDeck');
                window.location.reload();
            } catch (err) {
                setError(err.response?.data?.error || err.message || 'Wipe failed');
                setIsWiping(false);
            }
        };

        return (
            <div className="data-wipe">
                <h2>Danger zone</h2>
                <p>Deletes every player account, collection, deck, shop claim, wallet, match reward, and analytics row. Card effects and app config stay.</p>
                <button type="button" className="data-wipe-open" onClick={() => setIsOpen(true)}>
                    Wipe all player data
                </button>
                {isOpen && (
                    <div className="data-wipe-modal" role="dialog" aria-modal="true" aria-labelledby="data-wipe-title">
                        <form className="data-wipe-dialog" onSubmit={wipe}>
                            <h3 id="data-wipe-title">Wipe all player data?</h3>
                            <p>This cannot be undone. Every player loses their cards, decks, coins, shop claims, and match history. Your login stays, but this account’s progress is wiped too.</p>
                            <p>Type <strong>{WIPE_CONFIRMATION}</strong> to continue.</p>
                            <input
                                value={confirmation}
                                onChange={(e) => setConfirmation(e.target.value)}
                                disabled={isWiping}
                                autoComplete="off"
                                spellCheck={false}
                                aria-label="Confirmation phrase"
                            />
                            {error && <div className="data-wipe-error">{error}</div>}
                            <div className="data-wipe-actions">
                                <button type="button" onClick={close} disabled={isWiping}>Cancel</button>
                                <button type="submit" className="data-wipe-confirm" disabled={!canWipe}>
                                    {isWiping ? 'Wiping...' : 'Wipe data'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        );
    };

    const RecentGamesTable = () => {
        if (!recentGames || recentGames.length === 0) {
            return (
                <>
                    <h2>Recent Games</h2>
                    <div>No recent games</div>
                </>
            );
        }

        return (
            <>
                <h2>Recent Games</h2>
                <table className="recent-games-table">
                    <thead>
                        <tr>
                            <th>Player1</th>
                            <th>Player2</th>
                            <th>Start</th>
                            <th>End</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recentGames.map(game => (
                            <tr key={game.gameId}>
                                <td>{game.players[0]}</td>
                                <td>{game.players[1]}</td>
                                <td>{new Date(game.startTime).toLocaleString()}</td>
                                <td>{game.endTime ? new Date(game.endTime).toLocaleString() : 'N/A'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </>
        );
    };

    const DauAnalyticsTable = () => {
        if (!dauAnalytics || dauAnalytics.length === 0) {
            return (
                <>
                    <h2>DAU Analytics</h2>
                    <div>No DAU data</div>
                </>
            );
        }

        return (
            <>
                <h2>DAU Analytics</h2>
                <table className="recent-games-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Logged In</th>
                            <th>Clicked Find Match</th>
                            <th>Started Match</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dauAnalytics.map((day) => (
                            <tr key={day.date}>
                                <td>{day.date}</td>
                                <td>{day.playersLoggedIn}</td>
                                <td>{day.playersClickedFindMatch}</td>
                                <td>{day.playersStartedMatch}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </>
        );
    };

    return (
        <div className="admin-dashboard-page">
            <h1>Admin Dashboard</h1>
            { isLoadingConfig || isLoadingRecentGames || isLoadingDauAnalytics ?
                <div>Loading...</div> :
                <>
                    <OpenAlphaToggle />
                    <StartBotButtons />
                    <LoginAsNewUserButton />
                    <EffectsEditorButton />
                    <CollectionTools />
                    <WalletTools />
                    <DauAnalyticsTable />
                    <RecentGamesTable />
                    <DataWipe />
                </>
            }
        </div>
    );
};

export default AdminDashboardPage;